import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { TrendingUp, TrendingDown, DollarSign, Users, PieChart as PieChartIcon, AlertTriangle, ShieldCheck, ArrowLeft, BarChart3, Landmark, Building2, ChevronRight, UserCheck, X, Target, Shield, Gauge, Activity, TrendingDown as Trending } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { dashboardApi, api } from '../lib/api';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell, PieChart, Pie
} from 'recharts';

const fmt = (v) => v != null ? `R ${Number(v).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}` : '-';
const pct = (v) => v != null ? `${Number(v).toFixed(2)}%` : '-';

const CATEGORY_COLORS = {
  profitability: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', bar: '#059669' },
  liquidity_capital: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', bar: '#2563EB' },
  asset_quality: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', bar: '#D97706' },
  efficiency: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', bar: '#7C3AED' },
};

const ExecutiveDashboardPage = () => {
  const [metrics, setMetrics] = useState(null);
  const [radarData, setRadarData] = useState(null);
  const [branchAgeing, setBranchAgeing] = useState([]);
  const [productDist, setProductDist] = useState([]);
  const [loanPhases, setLoanPhases] = useState([]);
  const [productPerf, setProductPerf] = useState([]);
  const [officerStats, setOfficerStats] = useState(null);
  const [offices, setOffices] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterBranch, setFilterBranch] = useState('all');
  const [filterManager, setFilterManager] = useState('all');
  const [filterOfficer, setFilterOfficer] = useState('all');
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview');
  const [alcoData, setAlcoData] = useState(null);
  const [covenantData, setCovenantData] = useState(null);
  const [fullCovenantReport, setFullCovenantReport] = useState(null);
  const [covenantLoading, setCovenantLoading] = useState(false);
  const [riskMonitor, setRiskMonitor] = useState(null);
  const [riskLoading, setRiskLoading] = useState(false);
  const [commission, setCommission] = useState(null);
  const [commissionLoading, setCommissionLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { load(); }, []);

  const loadFiltered = async (branch, manager, officer) => {
    const params = {};
    if (branch && branch !== 'all') params.branch = branch;
    if (manager && manager !== 'all') params.manager = manager;
    if (officer && officer !== 'all') params.loan_officer = officer;
    const safe = async (fn) => { try { const r = await fn(); return r.data; } catch { return null; } };
    const [m, os, ba, pd, lp, pp] = await Promise.all([
      safe(() => api.get('/executive/overview-metrics', { params })),
      safe(() => api.get('/executive/loan-officer-stats', { params })),
      safe(() => api.get('/dashboard/loans-by-branch-ageing', { params })),
      safe(() => api.get('/dashboard/product-distribution', { params })),
      safe(() => api.get('/dashboard/loans-by-phase', { params })),
      safe(() => api.get('/dashboard/product-performance', { params })),
    ]);
    if (m) setMetrics(m);
    if (os) setOfficerStats(os);
    setBranchAgeing(Array.isArray(ba) ? ba : []);
    setProductDist(Array.isArray(pd) ? pd : []);
    setLoanPhases(Array.isArray(lp) ? lp : []);
    setProductPerf(Array.isArray(pp) ? pp : []);
  };

  const onFilterChange = (field, value) => {
    const newBranch = field === 'branch' ? value : filterBranch;
    const newManager = field === 'manager' ? value : filterManager;
    const newOfficer = field === 'officer' ? value : filterOfficer;
    if (field === 'branch') setFilterBranch(value);
    if (field === 'manager') setFilterManager(value);
    if (field === 'officer') setFilterOfficer(value);
    loadFiltered(newBranch, newManager, newOfficer);
  };

  const load = async () => {
    setLoading(true);
    const safe = async (fn) => { try { const r = await fn(); return r.data; } catch { return null; } };
    const [m, rd, ba, pd, lp, pp, os, off, usr, basicStats] = await Promise.all([
      safe(() => api.get('/executive/overview-metrics')),
      safe(() => dashboardApi.executive.radarData()),
      safe(() => api.get('/dashboard/loans-by-branch-ageing')),
      safe(() => api.get('/dashboard/product-distribution')),
      safe(() => api.get('/dashboard/loans-by-phase')),
      safe(() => api.get('/dashboard/product-performance')),
      safe(() => dashboardApi.executive.loanOfficerStats()),
      safe(() => api.get('/offices')),
      safe(() => api.get('/auth/users')),
      safe(() => dashboardApi.stats()),
    ]);
    // Use executive metrics if available, otherwise build from basic dashboard stats
    if (m) {
      setMetrics(m);
    } else if (basicStats) {
      setMetrics({
        profitability: {},
        liquidity_capital: {},
        asset_quality: {},
        efficiency: {},
        summary: {
          total_assets: (basicStats.loan_portfolio || 0) + (basicStats.total_deposits || 0),
          total_outstanding: basicStats.loan_portfolio || 0,
          total_deposits: basicStats.total_deposits || 0,
          total_revenue: 0,
          total_expenses: 0,
          equity: 0,
          loan_count: basicStats.total_loans || 0,
          client_count: basicStats.total_clients || 0,
        },
      });
    }
    if (rd) setRadarData(rd);
    setBranchAgeing(Array.isArray(ba) ? ba : []);
    setProductDist(Array.isArray(pd) ? pd : []);
    setLoanPhases(Array.isArray(lp) ? lp : []);
    setProductPerf(Array.isArray(pp) ? pp : []);
    if (os) setOfficerStats(os);
    setOffices(Array.isArray(off) ? off : []);
    setUsers(Array.isArray(usr) ? usr : []);
    setLoading(false);
    // Load ALCO data (non-blocking)
    safe(() => api.get('/accounting/alm/alco-dashboard')).then(d => { if (d) setAlcoData(d); });
    safe(() => api.get('/accounting/covenant-check/institution')).then(d => { if (d) setCovenantData(d); });
  };

  if (loading) return <div className="p-6"><div className="text-center py-16 text-gray-400">Loading executive data...</div></div>;

  const totalProducts = Array.isArray(productDist) ? productDist.reduce((s, d) => s + d.value, 0) : 0;
  const managers = users.filter(u => u.role === 'manager');
  const loanOfficers = users.filter(u => u.role === 'loan_officer');

  return (
    <div className="space-y-6 fade-in" data-testid="executive-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="exec-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">MANAGEMENT VIEW</p>
            <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Executive Dashboard</h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex border border-gray-200 overflow-hidden" data-testid="view-toggle">
            {[{ id: 'overview', label: 'Overview' }, { id: 'officers', label: 'Loan Officers' }, { id: 'alco', label: 'ALCO' }, { id: 'risk', label: 'Risk Monitor' }, { id: 'commission', label: 'Commission' }].map(v => (
              <button key={v.id} onClick={() => {
                  setActiveView(v.id);
                  if (v.id === 'risk' && !riskMonitor) {
                    setRiskLoading(true);
                    api.get('/executive/credit-score-monitor')
                      .then(r => setRiskMonitor(r.data))
                      .catch(() => setRiskMonitor({ error: true }))
                      .finally(() => setRiskLoading(false));
                  }
                  if (v.id === 'commission' && !commission) {
                    setCommissionLoading(true);
                    api.get('/insurance/reports/commission-income?months=12')
                      .then(r => setCommission(r.data))
                      .catch(() => setCommission({ error: true }))
                      .finally(() => setCommissionLoading(false));
                  }
                }}
                className={`px-4 py-1.5 text-xs font-medium transition-colors ${activeView === v.id ? 'bg-[#1f1f1f] text-white' : 'text-gray-600 hover:bg-gray-50'}`}
                data-testid={`view-${v.id}`}>{v.label}</button>
            ))}
          </div>
          <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#C5A059]" /><span className="text-xs text-gray-500 uppercase tracking-wider">Restricted</span></div>
        </div>
      </div>

      {/* ============ OVERVIEW TAB ============ */}
      {activeView === 'overview' && metrics && (
        <>
          {/* 4 Metric Category Cards with Gauges */}
          {Object.keys(metrics.profitability || {}).length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <MetricCategory title="Profitability" icon={<TrendingUp className="w-5 h-5" />} category="profitability" data={metrics.profitability} />
            <MetricCategory title="Liquidity & Capital" icon={<Shield className="w-5 h-5" />} category="liquidity_capital" data={metrics.liquidity_capital} />
            <MetricCategory title="Asset Quality" icon={<Target className="w-5 h-5" />} category="asset_quality" data={metrics.asset_quality} />
            <MetricCategory title="Efficiency" icon={<Gauge className="w-5 h-5" />} category="efficiency" data={metrics.efficiency} />
          </div>
          )}

          {/* Industry Benchmarking Radar */}
          {radarData && (
            <div className="bg-white border border-gray-200 p-6" data-testid="radar-chart">
              <h3 className="font-semibold text-[#1f1f1f] mb-1 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-[#C5A059]" />Industry Benchmarking</h3>
              <p className="text-xs text-gray-500 mb-4">Institution vs SA banking industry averages</p>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={radarData.metrics} cx="50%" cy="50%" outerRadius="75%">
                  <PolarGrid stroke="#e5e7eb" />
                  <PolarAngleAxis dataKey="metric" tick={{ fontSize: 10, fill: '#6B7280' }} />
                  <PolarRadiusAxis tick={{ fontSize: 9, fill: '#9CA3AF' }} />
                  <Radar name="Your Institution" dataKey="institution" stroke="#1f1f1f" fill="#1f1f1f" fillOpacity={0.15} strokeWidth={2} />
                  <Radar name="Industry Average" dataKey="industry" stroke="#C5A059" fill="#C5A059" fillOpacity={0.1} strokeWidth={2} strokeDasharray="5 5" />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                  <Tooltip contentStyle={{ fontSize: 11, border: '1px solid #e5e7eb', borderRadius: 0 }} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Summary KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SummaryCard label="Total Assets" value={fmt(metrics.summary.total_assets)} />
            <SummaryCard label="Loan Portfolio" value={fmt(metrics.summary.total_outstanding)} />
            <SummaryCard label="Total Deposits" value={fmt(metrics.summary.total_deposits)} />
            <SummaryCard label="Active Clients" value={metrics.summary.client_count} />
          </div>
        </>
      )}

      {/* ============ LOAN OFFICERS TAB ============ */}
      {activeView === 'officers' && (
        <>
          {/* Drill-down Filters */}
          <div className="bg-white border border-gray-200 p-4 flex flex-wrap gap-4 items-end" data-testid="officer-filters">
            <div className="min-w-[180px]">
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Branch</label>
              <Select value={filterBranch} onValueChange={v => onFilterChange('branch', v)}>
                <SelectTrigger data-testid="filter-branch"><SelectValue placeholder="All Branches" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {offices.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[180px]">
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Manager</label>
              <Select value={filterManager} onValueChange={v => onFilterChange('manager', v)}>
                <SelectTrigger data-testid="filter-manager"><SelectValue placeholder="All Managers" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Managers</SelectItem>
                  {managers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[180px]">
              <label className="text-xs text-gray-500 uppercase tracking-wider block mb-1">Loan Officer</label>
              <Select value={filterOfficer} onValueChange={v => onFilterChange('officer', v)}>
                <SelectTrigger data-testid="filter-officer"><SelectValue placeholder="All Officers" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Officers</SelectItem>
                  {loanOfficers.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Same dashboard layout as main dashboard */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <SummaryCard label="Active Clients" value={metrics?.summary?.client_count || 0} />
            <SummaryCard label="Loan Portfolio" value={fmt(metrics?.summary?.total_outstanding || 0)} />
            <SummaryCard label="Total Deposits" value={fmt(metrics?.summary?.total_deposits || 0)} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-[#1f1f1f] mb-4">Loans by Branch and Ageing Status</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={branchAgeing} layout="vertical" margin={{ left: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 10, fill: '#9CA3AF' }} tickLine={false} axisLine={false} />
                  <YAxis dataKey="branch" type="category" tick={{ fontSize: 11, fill: '#374151' }} tickLine={false} axisLine={false} width={100} />
                  <Tooltip contentStyle={{ fontSize: 12, border: '1px solid #e5e7eb', borderRadius: 0 }} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Current" stackId="a" fill="#1f1f1f" />
                  <Bar dataKey="30-60 Days" stackId="a" fill="#C5A059" />
                  <Bar dataKey="60-90 Days" stackId="a" fill="#D97706" />
                  <Bar dataKey="90+ Days" stackId="a" fill="#DC2626" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white border border-gray-200 p-6">
              <h3 className="text-sm font-semibold text-[#1f1f1f] mb-4">Product Distribution</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={productDist} cx="50%" cy="50%" innerRadius={50} outerRadius={85} dataKey="value" nameKey="name"
                    label={({ name, value }) => `${name} ${totalProducts > 0 ? Math.round(value / totalProducts * 100) : 0}%`} labelLine={false}>
                    {productDist.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Loan Officer Performance Table */}
          {officerStats && (
            <div className="bg-white border border-gray-200 p-6" data-testid="officer-stats-view">
              <h3 className="font-semibold text-[#1f1f1f] mb-4 flex items-center gap-2"><UserCheck className="w-5 h-5 text-[#C5A059]" />Loan Officer Performance</h3>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="officers-table">
                  <thead><tr className="bg-gray-50 border-b">
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Officer</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Active</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Total</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Clients</th>
                    <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Outstanding</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Disbursed</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Delinquency %</th>
                  </tr></thead>
                  <tbody>
                    {officerStats.filter(o => {
                      if (filterOfficer !== 'all' && o.officer_id !== filterOfficer) return false;
                      return true;
                    }).map((o, i) => {
                      const maxOut = Math.max(...officerStats.map(os => os.outstanding), 1);
                      return (
                        <tr key={o.officer_id || i} className="border-b hover:bg-gray-50">
                          <td className="px-4 py-3"><p className="text-sm font-medium text-[#1f1f1f]">{o.name}</p><p className="text-xs text-gray-400">{o.email}</p></td>
                          <td className="px-4 py-3 text-right font-mono text-sm">{o.active_loans}</td>
                          <td className="px-4 py-3 text-right font-mono text-sm">{o.total_loans}</td>
                          <td className="px-4 py-3 text-right font-mono text-sm">{o.clients_served}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-100 h-4 overflow-hidden"><div className="h-full bg-[#C5A059]" style={{ width: `${(o.outstanding / maxOut * 100)}%` }} /></div>
                              <span className="text-xs font-mono w-28 text-right">{fmt(o.outstanding)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-sm">{fmt(o.disbursed)}</td>
                          <td className="px-4 py-3 text-right">
                            <span className={`text-sm font-semibold ${o.delinquency_rate > 5 ? 'text-red-600' : o.delinquency_rate > 2 ? 'text-amber-600' : 'text-emerald-600'}`}>{pct(o.delinquency_rate)}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* ============ ALCO TAB ============ */}
      {activeView === 'alco' && (
        <div className="space-y-6" data-testid="alco-dashboard">
          {!alcoData ? (
            <div className="text-center py-16 text-gray-400">Loading ALCO data...</div>
          ) : (
            <>
              {/* Capital Adequacy + Liquidity Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <AlcoCard label="CAR" value={`${alcoData.capital_adequacy.car}%`}
                  status={alcoData.capital_adequacy.car_status} min={`Min ${alcoData.capital_adequacy.car_min}%`}
                  color={alcoData.capital_adequacy.car >= alcoData.capital_adequacy.car_min ? 'emerald' : 'red'} />
                <AlcoCard label="Loan-to-Deposit" value={`${alcoData.liquidity.ltd_ratio}%`}
                  status={alcoData.liquidity.ltd_ratio < alcoData.liquidity.ltd_threshold ? 'healthy' : 'warning'}
                  min={`Threshold ${alcoData.liquidity.ltd_threshold}%`}
                  color={alcoData.liquidity.ltd_ratio < alcoData.liquidity.ltd_threshold ? 'emerald' : 'amber'} />
                <AlcoCard label="LCR" value={`${alcoData.liquidity.lcr}%`}
                  status={alcoData.liquidity.lcr_status} min={`Min ${alcoData.liquidity.lcr_min}%`}
                  color={alcoData.liquidity.lcr >= alcoData.liquidity.lcr_min ? 'emerald' : 'red'} />
                <AlcoCard label="NSFR" value={`${alcoData.liquidity.nsfr}%`}
                  status={alcoData.liquidity.nsfr_status} min={`Min ${alcoData.liquidity.nsfr_min}%`}
                  color={alcoData.liquidity.nsfr >= alcoData.liquidity.nsfr_min ? 'emerald' : 'red'} />
              </div>

              {/* Capital Adequacy Detail */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 p-6" data-testid="capital-adequacy">
                  <h3 className="text-sm font-semibold text-[#1f1f1f] mb-1 flex items-center gap-2"><Shield className="w-4 h-4 text-[#C5A059]" />Capital Adequacy (Basel III)</h3>
                  <p className="text-xs text-gray-400 mb-4">SARB minimum: 10% | Current: {alcoData.capital_adequacy.car}%</p>
                  <div className="space-y-3">
                    <div className="flex justify-between text-xs"><span className="text-gray-500">Tier 1 Capital</span><span className="font-mono font-semibold">{fmt(alcoData.capital_adequacy.tier1_capital)}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-gray-500">Total RWA</span><span className="font-mono font-semibold">{fmt(alcoData.capital_adequacy.total_rwa)}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-gray-500">Leverage Ratio</span><span className="font-mono font-semibold">{alcoData.capital_adequacy.leverage_ratio}%</span></div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden mt-2">
                      <div className="h-full rounded-full transition-all" style={{
                        width: `${Math.min(alcoData.capital_adequacy.car / 30 * 100, 100)}%`,
                        backgroundColor: alcoData.capital_adequacy.car >= 10 ? '#059669' : '#DC2626',
                      }} />
                    </div>
                    <div className="flex justify-between text-[10px] text-gray-400">
                      <span>0%</span><span className="text-red-500">10% min</span><span>30%</span>
                    </div>
                  </div>
                </div>

                {/* RWA Breakdown */}
                <div className="bg-white border border-gray-200 p-6" data-testid="rwa-breakdown">
                  <h3 className="text-sm font-semibold text-[#1f1f1f] mb-1 flex items-center gap-2"><Target className="w-4 h-4 text-[#C5A059]" />Risk-Weighted Assets</h3>
                  <p className="text-xs text-gray-400 mb-4">Basel III Standardised Approach</p>
                  <div className="space-y-2">
                    {(alcoData.capital_adequacy.rwa_breakdown || []).filter(b => b.exposure > 0).map((b, i) => (
                      <div key={i} className="flex items-center gap-3 text-xs">
                        <span className="w-32 truncate text-gray-600">{b.category}</span>
                        <div className="flex-1 h-2 bg-gray-100 rounded overflow-hidden">
                          <div className="h-full bg-[#1f1f1f] rounded" style={{ width: `${Math.min(b.rwa / Math.max(alcoData.capital_adequacy.total_rwa, 1) * 100, 100)}%` }} />
                        </div>
                        <span className="w-14 text-right font-mono text-gray-500">{(b.risk_weight * 100).toFixed(0)}%</span>
                        <span className="w-24 text-right font-mono font-semibold">{fmt(b.rwa)}</span>
                      </div>
                    ))}
                    <div className="border-t pt-2 flex justify-between text-xs font-semibold">
                      <span>Total RWA</span><span className="font-mono">{fmt(alcoData.capital_adequacy.total_rwa)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* ALM Maturity Bucketing */}
              <div className="bg-white border border-gray-200 p-6" data-testid="maturity-buckets">
                <h3 className="text-sm font-semibold text-[#1f1f1f] mb-1 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#C5A059]" />ALM Maturity Bucketing</h3>
                <p className="text-xs text-gray-400 mb-4">Assets and liabilities grouped by time to maturity</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-gray-50 border-b">
                      <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-gray-500">Bucket</th>
                      <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-gray-500">Assets</th>
                      <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-gray-500">Liabilities</th>
                      <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-gray-500">Gap</th>
                      <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-gray-500">Cumulative Gap</th>
                      <th className="px-3 py-2 font-semibold uppercase tracking-wider text-gray-500">Visual</th>
                    </tr></thead>
                    <tbody>
                      {(alcoData.alm.maturity_buckets || []).map((b, i) => {
                        const maxVal = Math.max(...alcoData.alm.maturity_buckets.map(x => Math.max(x.assets, x.liabilities)), 1);
                        return (
                          <tr key={i} className="border-b hover:bg-gray-50">
                            <td className="px-3 py-2.5 font-medium text-gray-700">{b.bucket}</td>
                            <td className="px-3 py-2.5 text-right font-mono">{fmt(b.assets)}</td>
                            <td className="px-3 py-2.5 text-right font-mono">{fmt(b.liabilities)}</td>
                            <td className={`px-3 py-2.5 text-right font-mono font-semibold ${b.gap >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmt(b.gap)}</td>
                            <td className={`px-3 py-2.5 text-right font-mono font-semibold ${b.cumulative_gap >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{fmt(b.cumulative_gap)}</td>
                            <td className="px-3 py-2.5">
                              <div className="flex items-center gap-1 h-4">
                                <div className="bg-blue-500 h-full rounded-sm" style={{ width: `${(b.assets / maxVal) * 60}px` }} title={`Assets: ${fmt(b.assets)}`} />
                                <div className="bg-amber-500 h-full rounded-sm" style={{ width: `${(b.liabilities / maxVal) * 60}px` }} title={`Liabilities: ${fmt(b.liabilities)}`} />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="flex items-center gap-4 mt-2 text-[10px] text-gray-400">
                    <span className="flex items-center gap-1"><div className="w-3 h-2 bg-blue-500 rounded-sm" />Assets</span>
                    <span className="flex items-center gap-1"><div className="w-3 h-2 bg-amber-500 rounded-sm" />Liabilities</span>
                    <span>Negative gap = more liabilities maturing than assets (funding risk)</span>
                  </div>
                </div>
              </div>

              {/* Duration Gap + Interest Rate Sensitivity */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white border border-gray-200 p-6" data-testid="duration-gap">
                  <h3 className="text-sm font-semibold text-[#1f1f1f] mb-1 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#C5A059]" />Duration Gap Analysis</h3>
                  <p className="text-xs text-gray-400 mb-4">Interest rate sensitivity measurement</p>
                  <div className="space-y-3">
                    <div className="flex justify-between text-xs"><span className="text-gray-500">Avg Asset Duration</span><span className="font-mono font-semibold">{alcoData.alm.duration_gap.avg_asset_duration_years} years</span></div>
                    <div className="flex justify-between text-xs"><span className="text-gray-500">Avg Liability Duration</span><span className="font-mono font-semibold">{alcoData.alm.duration_gap.avg_liability_duration_years} years</span></div>
                    <div className="border-t pt-2 flex justify-between text-xs"><span className="text-gray-600 font-semibold">Duration Gap</span><span className={`font-mono font-bold text-base ${alcoData.alm.duration_gap.duration_gap > 0 ? 'text-amber-600' : 'text-blue-600'}`}>{alcoData.alm.duration_gap.duration_gap} years</span></div>
                    <p className="text-[10px] text-gray-400 leading-relaxed mt-1">{alcoData.alm.duration_gap.gap_interpretation}</p>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 p-6" data-testid="rate-sensitivity">
                  <h3 className="text-sm font-semibold text-[#1f1f1f] mb-1 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" />Interest Rate Shock</h3>
                  <p className="text-xs text-gray-400 mb-4">Impact of 100bps (1%) rate increase on equity</p>
                  <div className="space-y-3">
                    <div className="flex justify-between text-xs"><span className="text-gray-500">Rate Shock</span><span className="font-mono font-semibold">+100 bps</span></div>
                    <div className="flex justify-between text-xs"><span className="text-gray-500">Equity Impact</span><span className={`font-mono font-bold text-base ${alcoData.alm.duration_gap.interest_rate_sensitivity.equity_impact < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{fmt(alcoData.alm.duration_gap.interest_rate_sensitivity.equity_impact)}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-gray-500">Direction</span><span className="font-semibold">{alcoData.alm.duration_gap.interest_rate_sensitivity.impact_direction}</span></div>
                  </div>
                </div>
              </div>

              {/* Summary Bar */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <SummaryCard label="Total Assets" value={fmt(alcoData.summary.total_assets)} />
                <SummaryCard label="Total Deposits" value={fmt(alcoData.summary.total_liabilities)} />
                <SummaryCard label="Total Equity" value={fmt(alcoData.summary.total_equity)} />
                <SummaryCard label="Total Loans" value={fmt(alcoData.summary.total_loans)} />
                <SummaryCard label="Net Income" value={fmt(alcoData.summary.net_income)} />
              </div>

              {/* Covenant Health Check */}
              {covenantData && (
                <div className="bg-white border border-gray-200 p-6" data-testid="covenant-health">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="text-sm font-semibold text-[#1f1f1f] flex items-center gap-2"><Shield className="w-4 h-4 text-[#C5A059]" />Covenant Health Check</h3>
                      <p className="text-xs text-gray-400">Institution-level compliance (SARB/Basel III)</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-3 py-1 text-xs font-bold rounded-full ${covenantData.overall_status === 'healthy' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                        {covenantData.overall_status === 'healthy' ? 'ALL COMPLIANT' : 'BREACH DETECTED'}
                      </span>
                      <button
                        onClick={async () => {
                          setCovenantLoading(true);
                          try {
                            const res = await api.post('/accounting/covenant-check');
                            setFullCovenantReport(res.data);
                            setCovenantData(res.data.institution);
                          } catch (e) { console.error(e); }
                          setCovenantLoading(false);
                        }}
                        disabled={covenantLoading}
                        className="px-3 py-1.5 text-xs font-medium bg-[#1f1f1f] text-white rounded hover:bg-[#333333] disabled:opacity-50 transition-all"
                        data-testid="run-covenant-check-btn"
                      >
                        {covenantLoading ? 'Running...' : 'Run Full Check'}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {covenantData.covenants?.map((c, i) => (
                      <div key={i} className="flex items-center gap-3 text-xs py-1.5 border-b border-gray-50 last:border-0">
                        <span className={`w-2 h-2 rounded-full ${c.compliant ? (c.status === 'warning' ? 'bg-amber-500' : 'bg-emerald-500') : 'bg-red-500'}`} />
                        <span className="flex-1 text-gray-600">{c.label}</span>
                        <span className="font-mono font-semibold w-20 text-right">{c.current_value.toFixed(2)}%</span>
                        <span className="text-gray-400 w-16 text-right">{c.operator} {c.threshold}%</span>
                        <span className={`w-16 text-right font-semibold capitalize ${c.status === 'healthy' ? 'text-emerald-600' : c.status === 'warning' ? 'text-amber-600' : 'text-red-600'}`}>{c.status}</span>
                      </div>
                    ))}
                  </div>

                  {/* Loan-Level Covenant Results (shown after Run Full Check) */}
                  {fullCovenantReport?.loan_level && (
                    <div className="mt-5 pt-4 border-t border-gray-200">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-xs font-semibold text-[#1f1f1f] flex items-center gap-2">
                          <Target className="w-3.5 h-3.5 text-[#C5A059]" />Loan-Level Covenants
                        </h4>
                        <span className="text-[10px] text-gray-400">
                          {fullCovenantReport.loan_level.total_loans_checked} loans checked | {fullCovenantReport.loan_level.loans_in_breach} in breach
                        </span>
                      </div>
                      {fullCovenantReport.loan_level.total_loans_checked === 0 ? (
                        <p className="text-xs text-gray-400 italic">No SME/corporate/secured loans with applicable covenants found.</p>
                      ) : (
                        <div className="space-y-2">
                          {fullCovenantReport.loan_level.loans?.map((l, i) => (
                            <div key={i} className={`p-3 rounded border ${l.breached ? 'border-red-200 bg-red-50' : 'border-gray-100 bg-gray-50'}`}>
                              <div className="flex items-center justify-between mb-1.5">
                                <div className="flex items-center gap-2">
                                  <span className={`w-2 h-2 rounded-full ${l.breached ? 'bg-red-500' : 'bg-emerald-500'}`} />
                                  <span className="text-xs font-semibold text-gray-700">{l.loan_number}</span>
                                  <span className="text-[10px] text-gray-400">{l.client_name}</span>
                                </div>
                                <span className="text-[10px] text-gray-500 bg-gray-200 px-2 py-0.5 rounded">{l.category?.replace(/_/g, ' ')}</span>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                {l.covenants?.map((c, j) => (
                                  <div key={j} className="flex items-center gap-1.5 text-[10px]">
                                    <span className={`w-1.5 h-1.5 rounded-full ${c.compliant ? 'bg-emerald-500' : 'bg-red-500'}`} />
                                    <span className="text-gray-500">{c.label.split(' ').slice(0, 2).join(' ')}</span>
                                    <span className={`font-mono font-semibold ${c.compliant ? 'text-gray-700' : 'text-red-600'}`}>{c.current_value.toFixed(2)}</span>
                                    <span className="text-gray-400">{c.operator}{c.threshold}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ============ RISK MONITOR TAB ============ */}
      {activeView === 'risk' && (
        <div className="space-y-6" data-testid="risk-monitor-tab">
          {riskLoading && <div className="text-center py-16 text-gray-400">Loading Credit Score Monitor...</div>}
          {!riskLoading && riskMonitor && !riskMonitor.error && (
            <>
              {/* Summary KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="risk-summary">
                <div className="bg-white border border-gray-200 p-4">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500">Total Clients</p>
                  <p className="text-2xl font-bold text-[#1f1f1f] mt-1">{riskMonitor.summary.total_clients}</p>
                </div>
                <div className="bg-white border border-gray-200 p-4">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500">Scored</p>
                  <p className="text-2xl font-bold text-[#1f1f1f] mt-1">{riskMonitor.summary.scored_clients}</p>
                </div>
                <div className="bg-red-50 border border-red-200 p-4" data-testid="flagged-low-card">
                  <p className="text-[10px] uppercase tracking-wider text-red-700">Score &lt; 500</p>
                  <p className="text-2xl font-bold text-red-700 mt-1">{riskMonitor.summary.flagged_low_score}</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 p-4" data-testid="flagged-drop-card">
                  <p className="text-[10px] uppercase tracking-wider text-amber-800">MoM Drop &gt; 50 pts</p>
                  <p className="text-2xl font-bold text-amber-800 mt-1">{riskMonitor.summary.flagged_mom_drop}</p>
                </div>
                <div className="bg-white border border-gray-200 p-4">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500">Deteriorated MoM</p>
                  <p className="text-2xl font-bold text-[#1f1f1f] mt-1">{riskMonitor.summary.deteriorated_count}</p>
                </div>
              </div>

              {/* Distribution: credit band × IFRS-9 stage */}
              <div className="bg-white border border-gray-200 p-6" data-testid="risk-distribution">
                <h3 className="text-sm font-semibold text-[#1f1f1f] mb-1 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-[#C5A059]" />Credit Band × IFRS-9 Stage Distribution
                </h3>
                <p className="text-xs text-gray-400 mb-4">Each row = a credit band; columns show how many active loans fall into each stage for clients in that band.</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-gray-500">Credit Band</th>
                        <th className="text-center px-3 py-2 font-semibold uppercase tracking-wider text-gray-500">Clients</th>
                        <th className="text-center px-3 py-2 font-semibold uppercase tracking-wider text-emerald-700">Stage 1</th>
                        <th className="text-center px-3 py-2 font-semibold uppercase tracking-wider text-amber-700">Stage 2</th>
                        <th className="text-center px-3 py-2 font-semibold uppercase tracking-wider text-red-700">Stage 3</th>
                        <th className="text-center px-3 py-2 font-semibold uppercase tracking-wider text-gray-400">Unstaged</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(riskMonitor.distribution || []).map((b, i) => {
                        const colorMap = { emerald: 'text-emerald-700', amber: 'text-amber-700', orange: 'text-orange-700', red: 'text-red-700', gray: 'text-gray-500' };
                        return (
                          <tr key={i} className="border-b hover:bg-gray-50" data-testid={`dist-row-${i}`}>
                            <td className={`px-3 py-2.5 font-medium ${colorMap[b.color] || ''}`}>{b.band}</td>
                            <td className="px-3 py-2.5 text-center font-mono font-semibold">{b.total}</td>
                            <td className="px-3 py-2.5 text-center font-mono">{b.stage_1}</td>
                            <td className="px-3 py-2.5 text-center font-mono">{b.stage_2}</td>
                            <td className="px-3 py-2.5 text-center font-mono">{b.stage_3}</td>
                            <td className="px-3 py-2.5 text-center font-mono text-gray-400">{b.unstaged}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Top 20 Deteriorated */}
              <div className="bg-white border border-gray-200 p-6" data-testid="top-deteriorated">
                <h3 className="text-sm font-semibold text-[#1f1f1f] mb-1 flex items-center gap-2">
                  <Trending className="w-4 h-4 text-red-600" />Top 20 Deteriorated Clients (Month-on-Month)
                </h3>
                <p className="text-xs text-gray-400 mb-4">Ranked by the largest score drop between the two most recent scoring runs.</p>
                {riskMonitor.top_deteriorated.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No month-on-month drops yet. Run credit scoring at least twice to populate this list.</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-gray-500">Client</th>
                        <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-gray-500">Previous</th>
                        <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-gray-500">Latest</th>
                        <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-gray-500">Drop</th>
                      </tr>
                    </thead>
                    <tbody>
                      {riskMonitor.top_deteriorated.map((row, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50" data-testid={`deteriorated-${i}`}>
                          <td className="px-3 py-2.5 font-medium text-gray-700">{row.name || row.client_id}</td>
                          <td className="px-3 py-2.5 text-right font-mono">{row.previous}</td>
                          <td className="px-3 py-2.5 text-right font-mono font-semibold">{row.latest}</td>
                          <td className={`px-3 py-2.5 text-right font-mono font-bold ${row.drop > 50 ? 'text-red-600' : 'text-amber-600'}`}>-{row.drop}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Flagged Clients with Sparklines */}
              <div className="bg-white border border-gray-200 p-6" data-testid="flagged-clients">
                <h3 className="text-sm font-semibold text-[#1f1f1f] mb-1 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />Flagged Clients — 12-Month Score Drift
                </h3>
                <p className="text-xs text-gray-400 mb-4">Clients with <b>score &lt; 500</b> or a <b>month-on-month drop &gt; 50 points</b>. Sparklines show the most recent 12 scoring runs.</p>
                {riskMonitor.flagged_clients.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No clients currently flagged. Run credit scoring to populate this list.</p>
                ) : (
                  <div className="space-y-2">
                    {riskMonitor.flagged_clients.map((c, i) => (
                      <div key={i} className="flex items-center gap-4 p-3 border border-gray-100 hover:bg-gray-50" data-testid={`flagged-${i}`}>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-[#1f1f1f]">{c.name}</p>
                          <div className="flex gap-2 mt-1">
                            {c.reasons.includes('score_below_500') && <span className="text-[10px] px-1.5 py-0.5 bg-red-100 text-red-700 rounded">SCORE &lt; 500</span>}
                            {c.reasons.includes('drop_over_50pts') && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded">DROP &gt; 50 PTS</span>}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] uppercase tracking-wider text-gray-400">Latest</p>
                          <p className={`text-lg font-bold ${c.credit_score < 500 ? 'text-red-600' : 'text-amber-700'}`}>{c.credit_score || '—'}</p>
                        </div>
                        {/* SVG Sparkline */}
                        <div className="w-40 h-12 flex items-end">
                          <Sparkline series={c.sparkline} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Last Run Footer */}
              {riskMonitor.last_run?.started_at && (
                <div className="text-[10px] text-gray-400 text-right" data-testid="risk-last-run">
                  Last scoring run: {new Date(riskMonitor.last_run.started_at).toLocaleString()} •
                  strategy: <b>{riskMonitor.last_run.strategy}</b> •
                  scored: {riskMonitor.last_run.clients_scored}/{riskMonitor.last_run.clients_processed} •
                  loans updated: {riskMonitor.last_run.loans_updated}
                </div>
              )}
            </>
          )}
          {!riskLoading && riskMonitor?.error && (
            <div className="text-center py-16 text-red-500">Failed to load credit score monitor.</div>
          )}
        </div>
      )}

      {/* ============ COMMISSION TAB ============ */}
      {activeView === 'commission' && (
        <div className="space-y-6" data-testid="commission-tab">
          {commissionLoading && <div className="text-center py-16 text-gray-400">Loading Commission Income...</div>}
          {!commissionLoading && commission && !commission.error && (
            <>
              {/* Totals */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="commission-totals">
                <div className="bg-emerald-50 border border-emerald-200 p-4">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700">Net Commission (12m)</p>
                  <p className="text-2xl font-bold text-emerald-700 mt-1">R {Number(commission.totals.net_commission_zar).toLocaleString()}</p>
                  <p className="text-[10px] text-gray-500 mt-1">After R {Number(commission.totals.refunds_zar).toLocaleString()} refunds</p>
                </div>
                <div className="bg-white border border-gray-200 p-4">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500">Premiums Written</p>
                  <p className="text-2xl font-bold text-[#1f1f1f] mt-1">R {Number(commission.totals.premium_written_zar).toLocaleString()}</p>
                </div>
                <div className="bg-white border border-gray-200 p-4">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500">Gross Commission</p>
                  <p className="text-2xl font-bold text-[#1f1f1f] mt-1">R {Number(commission.totals.commission_earned_zar).toLocaleString()}</p>
                </div>
                <div className="bg-white border border-gray-200 p-4">
                  <p className="text-[10px] uppercase tracking-wider text-gray-500">Claims Paid</p>
                  <p className="text-2xl font-bold text-[#1f1f1f] mt-1">R {Number(commission.totals.claims_paid_zar).toLocaleString()}</p>
                </div>
                <div className={`p-4 border ${commission.totals.loss_ratio_percent > 60 ? 'bg-red-50 border-red-200' : 'bg-white border-gray-200'}`}>
                  <p className="text-[10px] uppercase tracking-wider text-gray-500">Loss Ratio</p>
                  <p className={`text-2xl font-bold mt-1 ${commission.totals.loss_ratio_percent > 60 ? 'text-red-700' : 'text-[#1f1f1f]'}`}>{commission.totals.loss_ratio_percent}%</p>
                </div>
              </div>

              {/* Monthly Trend */}
              <div className="bg-white border border-gray-200 p-6" data-testid="commission-monthly">
                <h3 className="text-sm font-semibold text-[#1f1f1f] mb-4 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-[#C5A059]" />Monthly Commission Trend (last 12 months)
                </h3>
                <div className="space-y-1">
                  {commission.monthly.map((m, i) => {
                    const max = Math.max(...commission.monthly.map(x => x.commission_earned_zar)) || 1;
                    const pct = (m.commission_earned_zar / max) * 100;
                    return (
                      <div key={i} className="flex items-center gap-3 text-xs" data-testid={`comm-month-${m.period}`}>
                        <span className="w-16 text-gray-500 font-mono">{m.period}</span>
                        <div className="flex-1 bg-gray-100 h-6 relative">
                          <div className="bg-[#C5A059] h-6 transition-all" style={{ width: `${pct}%` }}></div>
                          <span className="absolute inset-0 flex items-center px-2 font-mono">R{Number(m.commission_earned_zar).toLocaleString()}</span>
                        </div>
                        <span className="w-28 text-right font-mono text-gray-500">R{Number(m.premiums_written_zar).toLocaleString()} prem</span>
                        <span className={`w-14 text-right font-mono ${m.loss_ratio_percent > 60 ? 'text-red-600' : 'text-gray-500'}`}>{m.loss_ratio_percent}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* By Insurer */}
              <div className="bg-white border border-gray-200 p-6" data-testid="commission-by-insurer">
                <h3 className="text-sm font-semibold text-[#1f1f1f] mb-4 flex items-center gap-2">
                  <Landmark className="w-4 h-4 text-[#C5A059]" />By Insurer (12 months)
                </h3>
                {commission.by_insurer.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">No commission earned yet. Configure CLI schemes and issue policies.</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-gray-500">Insurer</th>
                        <th className="text-center px-3 py-2 font-semibold uppercase tracking-wider text-gray-500">Active Policies</th>
                        <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-gray-500">Premiums Written</th>
                        <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-emerald-700">Commission</th>
                        <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-gray-500">Claims Paid</th>
                        <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-gray-500">Loss Ratio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commission.by_insurer.map((ins, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50" data-testid={`insurer-${i}`}>
                          <td className="px-3 py-2.5 font-medium">{ins.insurer}</td>
                          <td className="px-3 py-2.5 text-center font-mono">{ins.policies_active}</td>
                          <td className="px-3 py-2.5 text-right font-mono">R {Number(ins.premium_zar).toLocaleString()}</td>
                          <td className="px-3 py-2.5 text-right font-mono font-semibold text-emerald-700">R {Number(ins.commission_zar).toLocaleString()}</td>
                          <td className="px-3 py-2.5 text-right font-mono">R {Number(ins.claims_paid_zar).toLocaleString()}</td>
                          <td className={`px-3 py-2.5 text-right font-mono ${ins.loss_ratio_percent > 60 ? 'text-red-600 font-bold' : ''}`}>{ins.loss_ratio_percent}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
          {!commissionLoading && commission?.error && (
            <div className="text-center py-16 text-red-500">Failed to load commission income.</div>
          )}
        </div>
      )}
    </div>
  );
};

/* ============ SPARKLINE ============ */
const Sparkline = ({ series }) => {
  if (!series || series.length < 2) {
    return <div className="text-[10px] text-gray-400 italic">insufficient history</div>;
  }
  const scores = series.map(s => s.score).filter(v => v != null);
  const min = Math.min(...scores);
  const max = Math.max(...scores);
  const range = Math.max(max - min, 1);
  const W = 160, H = 40;
  const step = W / (scores.length - 1);
  const points = scores.map((v, i) => `${i * step},${H - ((v - min) / range) * H}`).join(' ');
  const last = scores[scores.length - 1];
  const first = scores[0];
  const down = last < first;
  return (
    <svg width={W} height={H} className="overflow-visible">
      <polyline fill="none" stroke={down ? '#DC2626' : '#059669'} strokeWidth="1.5" points={points} />
      <circle cx={W} cy={H - ((last - min) / range) * H} r="2.5" fill={down ? '#DC2626' : '#059669'} />
    </svg>
  );
};

/* ============ METRIC CATEGORY COMPONENT ============ */
const MetricCategory = ({ title, icon, category, data }) => {
  const colors = CATEGORY_COLORS[category];
  const metricsArr = Object.entries(data).map(([key, m]) => ({ key, ...m }));
  const chartData = metricsArr.map(m => ({
    name: m.label.length > 12 ? m.label.substring(0, 12) + '..' : m.label,
    value: m.value,
    benchmark: m.benchmark,
    fullLabel: m.label,
  }));

  return (
    <div className={`bg-white border border-gray-200 p-5`} data-testid={`metric-${category}`}>
      <div className="flex items-center gap-2 mb-4">
        <div className={`p-2 ${colors.bg} ${colors.text}`}>{icon}</div>
        <h3 className="text-sm font-semibold text-[#1f1f1f]">{title}</h3>
      </div>
      {/* Metric values */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {metricsArr.map(m => (
          <div key={m.key} className="p-2.5 bg-gray-50 border border-gray-100">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">{m.label}</p>
            <div className="flex items-baseline gap-1.5 mt-0.5">
              <p className="text-lg font-bold text-[#1f1f1f]">{m.unit === 'R' ? `R${m.value.toLocaleString()}` : m.unit ? `${m.value}${m.unit}` : m.value}</p>
              <p className={`text-[10px] font-medium ${m.value >= m.benchmark ? 'text-emerald-600' : 'text-amber-600'}`}>
                vs {m.unit === 'R' ? `R${m.benchmark}` : m.unit ? `${m.benchmark}${m.unit}` : m.benchmark}
              </p>
            </div>
            {/* Mini progress bar */}
            <div className="mt-1.5 h-1 bg-gray-200 overflow-hidden">
              <div className="h-full transition-all" style={{
                width: `${Math.min(Math.max((m.value / Math.max(m.benchmark, 1)) * 100, 0), 150)}%`,
                backgroundColor: m.value >= m.benchmark ? '#059669' : '#D97706',
                maxWidth: '100%',
              }} />
            </div>
          </div>
        ))}
      </div>
      {/* Bar chart comparison */}
      <ResponsiveContainer width="100%" height={120}>
        <BarChart data={chartData} margin={{ left: 0, right: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="name" tick={{ fontSize: 9, fill: '#9CA3AF' }} tickLine={false} />
          <YAxis tick={{ fontSize: 9, fill: '#9CA3AF' }} tickLine={false} axisLine={false} />
          <Tooltip contentStyle={{ fontSize: 11, border: '1px solid #e5e7eb', borderRadius: 0 }} formatter={(v, n) => [typeof v === 'number' ? v.toFixed(2) : v, n]} />
          <Bar dataKey="value" name="Actual" fill={colors.bar} radius={[2, 2, 0, 0]} />
          <Bar dataKey="benchmark" name="Benchmark" fill="#E5E7EB" radius={[2, 2, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

const SummaryCard = ({ label, value }) => (
  <div className="bg-white border border-gray-200 p-4" data-testid={`summary-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
    <p className="text-xl font-bold text-[#1f1f1f] mt-1">{value}</p>
  </div>
);

const AlcoCard = ({ label, value, status, min, color }) => (
  <div className="bg-white border border-gray-200 p-4" data-testid={`alco-${label.toLowerCase().replace(/[\s/]+/g, '-')}`}>
    <p className="text-[10px] text-gray-400 uppercase tracking-wider">{label}</p>
    <p className={`text-2xl font-bold mt-1 text-${color}-600`}>{value}</p>
    <div className="flex items-center gap-1.5 mt-1">
      <span className={`inline-block w-1.5 h-1.5 rounded-full bg-${color}-500`} />
      <span className={`text-[10px] font-medium text-${color}-600 capitalize`}>{status}</span>
    </div>
    <p className="text-[10px] text-gray-400 mt-0.5">{min}</p>
  </div>
);

export default ExecutiveDashboardPage;
